import { useContext, useMemo } from "react";
import { Context as IntlContext } from "context/Intl";
import {
    MIN_RATING_ALL,
    MIN_RATING_OPTIONS,
    POSTED_PERIOD_ALL,
    POSTED_PERIOD_LAST_24H,
    POSTED_PERIOD_LAST_MONTH,
    POSTED_PERIOD_LAST_WEEK,
} from "components/ListPage/filters/inlineFilterConfig";
import type { FilterOption } from "components/ListPage/filters";
import type { ProviderFilterOptions } from "./types";

export const useProviderFilterOptions = (): ProviderFilterOptions => {
    const intl = useContext(IntlContext);
    const minRatingOptions = useMemo<FilterOption[]>(
        () =>
            MIN_RATING_OPTIONS.map((rating) => ({
                value: rating,
                label: String(intl.formatMessage("providers.list.filters.rating_value", { rating })),
            })),
        [intl]
    );
    const minRatingClearOption = useMemo<FilterOption>(
        () => ({
            value: MIN_RATING_ALL,
            label: String(intl.formatMessage("providers.list.filters.rating_all")),
        }),
        [intl]
    );
    // Filters on service created_at. This is an intentional TWO-84 Figma extension
    // to surface recently added providers.
    const postedPeriodOptions = useMemo<FilterOption[]>(
        () => [
            {
                value: POSTED_PERIOD_LAST_24H,
                label: String(intl.formatMessage("providers.list.filters.posted_last_24h")),
            },
            {
                value: POSTED_PERIOD_LAST_WEEK,
                label: String(intl.formatMessage("providers.list.filters.posted_last_week")),
            },
            {
                value: POSTED_PERIOD_LAST_MONTH,
                label: String(intl.formatMessage("providers.list.filters.posted_last_month")),
            },
        ],
        [intl]
    );
    const postedPeriodClearOption = useMemo<FilterOption>(
        () => ({
            value: POSTED_PERIOD_ALL,
            label: String(intl.formatMessage("providers.list.filters.posted_all")),
        }),
        [intl]
    );

    return {
        labels: {
            category: String(intl.formatMessage("providers.list.filters.category")),
            subcategory: String(intl.formatMessage("providers.list.filters.subcategory")),
            city: String(intl.formatMessage("providers.list.filters.city")),
            posted: String(intl.formatMessage("providers.list.filters.posted")),
            radius: String(intl.formatMessage("providers.list.filters.radius")),
            radiusAll: String(intl.formatMessage("providers.list.filters.radius_all")),
            radiusValue: (radius: number) =>
                String(intl.formatMessage("providers.list.filters.radius_kilometers", { radius })),
            rating: String(intl.formatMessage("providers.list.filters.rating")),
            selectedCount: (count: number) =>
                String(intl.formatMessage("providers.list.filters.selected_count", { count })),
            reset: intl.formatMessage("providers.list.filters.reset"),
        },
        minRatingOptions,
        minRatingClearOption,
        postedPeriodOptions,
        postedPeriodClearOption,
    };
};
